// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { Instance as TippyInstance } from "tippy.js";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { sendAutocompleteEvent } from "@insite/client-framework/OdpEvents/AutocompleteEvent";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

import { TippyPopupStyle } from "@guestsupply/App/Components/Tippy/Style";
import TippyPopup from "@guestsupply/App/Components/Tippy/TippyPopup";
import xHeaderUpdate from "@guestsupply/App/Handlers/XNG/Header";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";

import FancyScroll, { FancyScrollRefProps } from "@guestsupply/App/Components/Common/FancyScroll";
import { HeaderClickableStyle } from "@guestsupply/App/Components/Header/HeaderClickable";
import xSetSearchQuery from "@guestsupply/App/Handlers/XNG/SearchQueryHandler";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@guestsupply/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@guestsupply/Overrides/Widgets/Header/SearchHistory";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import Close from "@guestsupply/XDL/Icons/Close";
import Search from "@guestsupply/XDL/Icons/Search";
import { addScrollbarGutter, parseBoolean, removeScrollbarGutter, xScreen } from "@guestsupply/XDL/Utilities";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import produce from "immer";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";
import { css } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
        customSettings: { enableHardRedirects },
    } = getSettingsCollection(state);
    const session = getSession(state);
    const isEducationCustomer = parseBoolean(session.properties?.isEducationCustomer);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        // XNg-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        query: state.xSearchQuery.query,
        showSearchSectionOnMobile: state.xHeader.showSearchSectionOnMobile,
        isEducationCustomer,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
        currentBillTo: getCurrentBillToState(state).value,
        enableHardRedirects,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    // XNg-Changes
    xHeaderUpdate,
    xSetSearchQuery,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    isLoading: boolean;
    clickedProduct: boolean;
    // XNG-Changes
    autoCompleteModel?: AutocompleteModel & { productContexts: ProductContextModel[] };
    tippyInstance?: TippyInstance;
    isAutocompleteOpen?: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNg-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    tippyPopup?: TippyPopupStyle;
    inputWrapper?: InjectableCss;
    searchClickable?: HeaderClickableStyle;
    autocompleteColumnProductMobileDivider?: InjectableCss;
    autocompleteContainer?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
}

export const searchInputStyles: SearchInputStyles = {};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private parentMenu = React.createRef<HTMLDivElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    parentBoxRef = React.createRef<HTMLDivElement>();
    fancyScrollRef = React.createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);
        this.state = {
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.props.xSetSearchQuery({ query: "" });
                // <XNG-Changes>
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
                // </XNG-Changes>
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
        if (this.fancyScrollRef.current) {
            this.fancyScrollRef.current.updateScrollable();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, { passive: true });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                .concat(categories || [])
                .concat(brands || [])
                .concat(content || [])
            : (categories || [])
                .concat(brands || [])
                .concat(content || [])
                .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    // XNG-CHANGES
    closeButtonClick = () => {
        if (this.props.showSearchSectionOnMobile) {
            this.props.xHeaderUpdate({ key: "showSearchSectionOnMobile", value: false });
        }
    };

    doSearch = () => {
        if (this.props.showSearchSectionOnMobile) {
            this.props.xHeaderUpdate({ key: "showSearchSectionOnMobile", value: false });
        }

        if (this.props.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            const encodedQuery = encodeURIComponent(this.props.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.props.query, this.props.searchHistoryLimit);
        }
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.props.xSetSearchQuery({ query: "" });
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState({
                        autoCompleteModel: {
                            ...result,
                            productContexts: (result?.products || []).map(product => ({
                                product: {
                                    id: product.id!,
                                    productNumber: product.erpNumber,
                                    mediumImagePath: product.image,
                                    productTitle: product.title,
                                    canonicalUrl: product.url,
                                    uri: product.uri,
                                    canAddToCart: parseBoolean(product.properties.canAddToCart),
                                    manufacturerItem: product.manufacturerItemNumber,
                                    minimumOrderQty: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
                                    brand: {
                                        name: product.brandName,
                                        detailPagePath: product.brandDetailPagePath,
                                    },
                                    customerProductNumber: product.isNameCustomerOverride ? product.name : "",
                                    properties: {
                                        ...product.properties,
                                        displayName: product.name || "",
                                    } as any,
                                } as ProductModel,
                                productInfo: {
                                    productId: product.id!,
                                    productDetailPath: product.url,
                                    qtyOrdered: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
                                    unitOfMeasure: "",
                                },
                                onQtyOrderedChanged: (qty: number) => {
                                    const autoCompleteModel = produce(this.state.autoCompleteModel, draftedState => {
                                        if (!draftedState) {
                                            return;
                                        }
                                        const productIndex = draftedState.productContexts.findIndex(
                                            p => p.product.id === product.id,
                                        );
                                        draftedState.productContexts[productIndex].productInfo.qtyOrdered = qty;
                                    });
                                    this.setState({ autoCompleteModel });
                                },
                            })),
                        },
                    });
                    // <XNG-Changes>
                    this.updatePopoverVisibility(result);
                    // </XNG-Changes>
                    // this.openAutocomplete();
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.props.xSetSearchQuery({ query: newQuery });
        this.loadAutocomplete(newQuery);
        // XNG-Changes
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    openAutocomplete = () => {
        // XNG-Changes
        this.state.tippyInstance?.show();
        this.setState({ isAutocompleteOpen: true });
    };

    closeAutocomplete = () => {
        // XNG-Changes
        this.state.tippyInstance?.hide();
        this.setState({ isAutocompleteOpen: false });
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.props.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.props.xSetSearchQuery({ query: this.props.query });
                    this.doSearch();
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    // xng-change: add google event
    getDataLayerEvent = () => {
        const { xGoogleAnalytics, currentBillTo } = this.props;
        if (xGoogleAnalytics.includeSearchResult) {
            const dataLayer = getDataLayer();
            let resultCount = 0;
            if (this.state.autoCompleteModel) {
                const { categories, brands, content, products } = this.state.autoCompleteModel;
                resultCount =
                    (products?.length ?? 0) +
                    (categories?.length || 0) +
                    (content?.length || 0) +
                    (brands?.length || 0);
            }

            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.AUTOCOMPLETE,
                search_term: this.props.query,
                numSearchResults: resultCount,
                affilliation: currentBillTo?.companyName,
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    // xng-change: add google event
    // trackAutcompleteSearchResults = () => {
    //     // Make sure only to trigger for autocomplete results
    //     if (!this.props.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
    //         return;
    //     }

    //     const { categories, brands, content, products } = this.state.autoCompleteModel;
    //     const resultCount =
    //         (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
    //     if (resultCount > 0) {
    //         // Track an autocomplete of 1+ items
    //         this.props.sendSearchTracking({
    //             searchEvent: "autocompleteSearchResults",
    //             searchTerm: this.props.query,
    //             resultCount,
    //             productCount: products?.length,
    //             categoryCount: categories?.length,
    //             contentCount: content?.length,
    //             brandCount: brands?.length,
    //         });
    //         return;
    //     }

    //     // Track zero resultCount search results and triggers from this.doSearch
    //     this.props.sendSearchTracking({
    //         searchEvent: "searchResults",
    //         searchTerm: this.props.query,
    //         resultCount,
    //     });
    // };

    goToUrl = (url: string, isHardRedirect = false) => {
        const data = {
            searchQuery: this.props.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        // xng-change: add google event
        this.getDataLayerEvent();
        // if (track) {
        //     this.trackAutcompleteSearchResults();
        // }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        if (isHardRedirect && this.props.enableHardRedirects) {
            window.location.href = url;
        } else {
            this.props.history.push(url);
        }
    };

    // <XNG-Changes>
    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.props.query && this.props.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.props.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    updatePopoverVisibility(results: AutocompleteModel | undefined) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };
    // </XNG-Changes>

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }
        const {
            categories,
            brands,
            content,
            productContexts,
            products,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        return (
            <FancyScroll refElement={this.parentBoxRef} ref={this.fancyScrollRef} shadowHeight="12px">
                <StyledWrapper
                    ref={this.parentBoxRef}
                    data-test-selector="autocomplete-container"
                    {...styles.autocompleteContainer}
                >
                    <>
                        {isRetailSearchCompletionResults &&
                            ((suggestions && suggestions.length > 0) ||
                                (categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0) ||
                                (brands && brands.length > 0) ||
                                (popularBrands && popularBrands.length > 0)) && (
                                <StyledWrapper
                                    {...this.styles.autocompleteCommerceSearchWrapper}
                                    className="autocomplete-commerce-search-wrapper"
                                >
                                    {suggestions && suggestions.length > 0 && (
                                        <AutocompleteSuggestions
                                            suggestions={suggestions}
                                            focusedItem={this.state.focusedAutocompleteItem}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                        />
                                    )}
                                    {((categories && categories.length > 0) ||
                                        (popularCategories && popularCategories.length > 0)) && (
                                            <AutocompleteCategories
                                                categories={categories}
                                                popularCategories={popularCategories}
                                                focusedItem={this.state.focusedAutocompleteItem}
                                                goToUrl={this.goToUrl}
                                                extendedStyles={this.styles.autocompleteCategoriesStyles}
                                                isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                            />
                                        )}
                                    {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                        <AutocompleteBrands
                                            brands={brands}
                                            popularBrands={popularBrands}
                                            focusedItem={this.state.focusedAutocompleteItem}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={this.styles.autocompleteBrandsStyles}
                                            isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                        />
                                    )}
                                </StyledWrapper>
                            )}
                        {!isRetailSearchCompletionResults && (
                            <StyledWrapper
                                {...this.styles.autocompleteWrapper}
                                {...{
                                    width: (this.inputWrapperRef.current?.getBoundingClientRect().width || 300) - 4,
                                    hasContent:
                                        (categories && categories.length > 0) ||
                                        (brands && brands.length > 0) ||
                                        (content && content.length > 0),
                                }}
                                className="autocomplete-wrapper"
                            >
                                {((categories && categories.length > 0) ||
                                    (brands && brands.length > 0) ||
                                    (content && content.length > 0)) && (
                                        <StyledWrapper
                                            {...this.styles.autocompleteColumnWrapper}
                                            className="autocomplete-column-wrapper"
                                        >
                                            <AutocompleteCategories
                                                categories={categories}
                                                focusedItem={this.state.focusedAutocompleteItem}
                                                goToUrl={this.goToUrl}
                                                extendedStyles={this.styles.autocompleteCategoriesStyles}
                                            />
                                            <AutocompleteBrands
                                                brands={brands}
                                                focusedItem={this.state.focusedAutocompleteItem}
                                                goToUrl={this.goToUrl}
                                                extendedStyles={this.styles.autocompleteBrandsStyles}
                                            />
                                            <AutocompleteContent
                                                content={content}
                                                focusedItem={this.state.focusedAutocompleteItem}
                                                goToUrl={url => this.goToUrl(url, true)}
                                                extendedStyles={this.styles.autocompleteContentStyles}
                                            />
                                        </StyledWrapper>
                                    )}
                                {((productContexts && productContexts.length > 0) || this.props.isMobileScreen) && (
                                    <StyledWrapper
                                        {...this.styles.autocompleteColumnProductWrapper}
                                        className="autocomplete-column-wrapper"
                                        {...{
                                            hasContent:
                                                (categories && categories.length > 0) ||
                                                (brands && brands.length > 0) ||
                                                (content && content.length > 0),
                                        }}
                                    >
                                        <StyledWrapper {...this.styles.autocompleteColumnProductMobileDivider}>
                                            <Link {...styles.viewAllLink} onClick={this.doSearch}>
                                                {translate("View all")}
                                            </Link>
                                        </StyledWrapper>
                                        <AutocompleteProducts
                                            products={productContexts}
                                            focusedItem={this.state.focusedAutocompleteItem}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={this.styles.autocompleteProductsStyles}
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        )}
                    </>
                </StyledWrapper>
            </FancyScroll>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        if (!this.props.query && this.showAutocomplete) {
            return (
                <SearchHistory
                    focusedQuery={this.state.focusedSearchHistoryQuery}
                    goToUrl={this.goToUrl}
                    extendedStyles={styles.searchHistoryStyles}
                    width={(this.inputWrapperRef.current?.getBoundingClientRect().width || 300) - 4}
                    clearSearchHistoryCallback={this.clearSearchHistoryCallback}
                />
            );
        }
        if (this.props.query) {
            return this.renderAutocomplete();
        }
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;
        // XNG-Changes
        const { query, xHeaderUpdate, isTabletScreen, isMobileScreen } = this.props;

        if (this.props.isEducationCustomer) {
            return null;
        }

        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef}>
                <TextField
                    {...styles.input}
                    {...({ sizeVariant: isTabletScreen ? "small" : "large" } as any)}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    value={query}
                    onChange={event => this.handleInputChange(event, true)} // passing toggleAutoComplete as true because it's passed as an empty string when there is no input value
                    placeholder={translate("Search by Keyword, Item #, Model #, or Part #")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    iconClickableProps={{
                        type: "button",
                        onClick: this.props.showSearchSectionOnMobile ? this.closeButtonClick : this.doSearch,
                    }}
                    // XNG-Changes
                    onKeyDown={this.handlePopoverKeyDown}
                    aria-multiline="false"
                    aria-required="false"
                    onFocus={this.handleInputFocus}
                    {...{ validationRules: "max:125" }}
                    iconProps={{
                        src: this.props.showSearchSectionOnMobile ? Close : Search,
                        size: 15,
                        css: css`
                            ${xScreen("lte", "md")} {
                                margin-top: 4px;
                            }
                        `,
                    }}
                />
            </StyledWrapper>
        );
        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                {/* XNG-Changes: change popover with tippy */}
                {(this.props.showSearchSectionOnMobile || !isMobileScreen) && (
                    <TippyPopup
                        onCreate={instance => {
                            this.setState({ tippyInstance: instance });
                        }}
                        onShow={instance => {
                            xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_search` });
                            this.setState({ tippyInstance: instance });
                        }}
                        onHidden={instance => {
                            this.setState({ isAutocompleteOpen: false });
                            xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_search` });
                        }}
                        content={<>{this.renderSearchAutoCompleteContent()}</>}
                        trigger="manual"
                        offset={[0, 0]}
                        duration={[null, null]}
                        arrow={false}
                        maxWidth="none"
                        placement="bottom-start"
                        type="menu"
                        mobileMode="left-side"
                        showCloseButton={false}
                        appendTo={() => document.body}
                        extendedStyles={styles.tippyPopup}
                        popperOptions={{
                            strategy: isTabletScreen ? "fixed" : "absolute",
                            modifiers: [
                                {
                                    name: "preventOverflow",
                                    options: {
                                        padding: 0,
                                    },
                                },
                                {
                                    name: "computeStyles",
                                    options: {
                                        adaptive: !isTabletScreen, // false for mobile only
                                        roundOffsets: ({ x, y }: any) => ({
                                            x: isTabletScreen ? 0 : x,
                                            y: isTabletScreen ? 0 : y,
                                        }),
                                    },
                                },
                            ],
                        }}
                    >
                        {searchInput}
                    </TippyPopup>
                )}
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
